export const EASYBIB = 'easybib'
export const BIBME = 'bibme'
export const CITATION_MACHINE = 'citationmachine'
export const CHEGG = 'chegg'
export const CHEGG_NET = 'cheggnet'

export const BUSINESS_UNIT = 'wt'

export type Site = typeof EASYBIB | typeof BIBME | typeof CITATION_MACHINE | typeof CHEGG

export interface GapiWrapper {
  init: () => Promise<any>
  onApiLoad: () => Promise<any>
  openDocumentPicker: () => Promise<any>
  downloadDocx: (gdriveDocumentId: string) => Promise<any>
}

export type Extensions = '.doc' | '.docx' | '.gdocs' | '.odt' | '.rtf' | '.txt'

export type MimeTypes =
  | 'application/msword'
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  | 'application/vnd.google-apps.document'
  | 'application/vnd.oasis.opendocument.text'
  | 'text/rtf'
  | 'text/plain'

const LEFT = 'left'
const RIGHT = 'right'
export const CENTER = 'center'
export type TextAlignment = typeof LEFT | typeof RIGHT | typeof CENTER

export interface UploadConfig {
  mimeTypes: MimeTypes[]
  extensions: Extensions[]
  filesize: {
    min: number
    max: number
  }
  averageDuration: number
}

export interface WtaiUploadWidgetProps {
  /**
   * `h1` tag text within widget is configurable.
   */
  headingText?: string
  /**
   * `h2` tag text within widget is configurable.
   */
  subheadingText?: string
  /**
   * Function called when upload is complete.
   * If provided, the widget **will not redirect into the editor**.
   * Your app can handle this success event, and bypass redirect.
   * Then explicitly direct the user to their paper when your application
   * is ready.
   */
  onUploadSuccess?: (document?: any) => void
  /**
   * `editorBaseUrl` can be explicitly configured.
   * This can be useful in development if you want to direct the user
   * into a "PR" environment rather than the default editor.
   * Will default to staging or production values for the `site`
   * via url sniffing. See `src/standalone/isProduction.ts`
   */
  editorBaseUrl?: string
  /**
   * `citations-frontend` endpoint, for authenticating the writing experience.
   * For specific values, see `src/standalone/<site name>`
   * For documentation about writing-tools' auth architecture, see
   * https://github.com/easybib/citations-frontend/blob/master/docs/AUTH.md
   */
  authApiUrl: string
  /**
   * Function called when a new WritingTools JWT is granted, passing the JWT string.
   * For JWT contents see
   * https://github.com/easybib/citations-frontend/blob/master/docs/AUTH.md#wt-jwt-token
   */
  onAuthUpdate?: (jwt?: string) => void
  site: Site
  /**
   * Defaults to `true`.
   * Toggles whether the upload widget should destroy and fetch a fresh
   * auth token on init, or whether to trust an existing auth token.
   * When the widget is embedded in a Writing Tools application, we may
   * trust an existing auth token. When the widget is served in a non-WT app
   * or page, we should always fetch a fresh token.
   */
  initFreshToken?: boolean
  /**
   * To override defaults in development for new features.
   */
  wtaiUploadUrl?: string
  /**
   * To override defaults in development for new features.
   */
  wtaiExpressUrl?: string
  /**
   * Only allow the paper to be pasted, hiding other upload options
   */
  pasteOnly?: boolean
  /**
   * How to align headings within the widget: left, right, or center
   * defaults to center
   */
  alignText?: TextAlignment
  /**
   * Toggle initialization of `rio-sdk-web`, chegg's analytics
   * client. This allows us to avoid initializing the same client twice,
   * or to avoid dispatching event in environments not yet configured
   * for rsw.
   *
   */
  initRSW?: boolean
}

export interface LandingPageConfig
  extends Partial<
      Pick<
        WtaiUploadWidgetProps,
        'site' | 'pasteOnly' | 'alignText' | 'headingText' | 'subheadingText' | 'initRSW' | 'initFreshToken'
      >
    > {
  domId?: string
}
